import {
	type ReactNode,
	type RefCallback,
	type RefObject,
	useCallback,
	useLayoutEffect,
	useRef,
} from 'react';

import { useMenuItemDragAndDrop } from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/use-menu-item-drag-and-drop';

import { useElementRegistry } from '../../controllers/element-registry';
import type { ItemDragAndDropGroup, NavItemConfigWithCustomizationOptions } from '../../types';
import { getL1ItemData, isL1ItemData, isL1ItemDataInGroup } from '../dnd-data';
import { getDragPreviewContentForItem, getElemBeforeForItem } from '../get-drag-preview';

interface ItemDragAndDrop {
	draggableRef: RefCallback<HTMLAnchorElement | HTMLButtonElement> | undefined;
	dropIndicator: ReactNode | undefined;
	dropTargetRef: RefObject<HTMLDivElement> | undefined;
	isDragging: boolean;
	isItemDraggable: boolean;
	l1DragAndDropEnabled?: boolean;
	maybeDragPreview: ReactNode;
}

export function useItemDragAndDrop({
	item,
	id,
	isExpandable,
	isExpandOrFlyoutOpen,
	onExpandFlyoutToggle,
	peek,
	l1DragAndDropEnabled,
	group,
}: {
	group: ItemDragAndDropGroup;
	id: string;
	isExpandable: boolean;
	isExpandOrFlyoutOpen: boolean;
	item: NavItemConfigWithCustomizationOptions;
	l1DragAndDropEnabled?: boolean;
	onExpandFlyoutToggle: (open: boolean) => void;
	peek: boolean;
}): ItemDragAndDrop {
	const isItemDraggable =
		!!l1DragAndDropEnabled &&
		!peek &&
		(item.isHideable !== false || item.isReorderable !== false) &&
		// Custom items must explicitly opt-in to being draggable since they need to consume the refs for it to work
		(item.type !== 'custom' || !!item.isDraggable);

	const {
		draggableAnchorRef,
		draggableButtonRef,
		state: dragState,
		dragPreview,
		dropTargetRef,
		dropIndicator,
	} = useMenuItemDragAndDrop({
		...(isItemDraggable && {
			draggable: {
				getInitialData: () =>
					getL1ItemData({
						menuItemId: id,
						label: item.label,
						isHidden: false,
						isHideable: item.isHideable !== false,
						isReorderable: item.isReorderable !== false,
						group,
					}),
				getDragPreviewPieces: () => ({
					elemBefore: getElemBeforeForItem(item),
					content: getDragPreviewContentForItem(item),
				}),
			},
			dropTarget: {
				getData: (args) => {
					const { source } = args;

					return getL1ItemData({
						menuItemId: id,
						label: item.label,
						isHidden: false,
						isHideable: item.isHideable !== false,
						isReorderable: item.isReorderable !== false,
						group: source?.data?.group as ItemDragAndDropGroup,
					});
				},
				getOperations: () => ({
					'reorder-before': 'available',
					'reorder-after': 'available',
				}),
				canDrop: (args) => {
					const { source } = args;
					return (
						isL1ItemData(source.data) &&
						source.data.menuItemId !== id &&
						source.data.isReorderable === true &&
						isL1ItemDataInGroup(source.data, group)
					);
				},
			},
		}),
	});

	const registry = useElementRegistry();

	const registrationRef = useRef<(() => void) | null>(null);
	const draggableRef = useCallback(
		(element: HTMLAnchorElement | HTMLButtonElement | null) => {
			if (!element) {
				registrationRef.current?.();
				registrationRef.current = null;

				draggableAnchorRef.current = null;
				draggableButtonRef.current = null;

				return;
			}

			// Set to appropriate ref from the drag and drop hook
			if (element instanceof HTMLAnchorElement) {
				draggableAnchorRef.current = element;
			} else {
				draggableButtonRef.current = element;
			}

			// Unregister the previous element if it exists
			if (registrationRef.current) {
				registrationRef.current();
			}

			// Register the element with the registry
			registrationRef.current = registry.registerL1Item({
				id,
				element,
			});
		},
		[id, registry, draggableAnchorRef, draggableButtonRef, registrationRef],
	);

	const isDragging = !!isItemDraggable && dragState.type === 'dragging';
	const expandableClosedForDrag = useRef(false);

	useLayoutEffect(() => {
		if (!isExpandable || !isItemDraggable) {
			return;
		}
		if (isDragging && isExpandOrFlyoutOpen) {
			// Close the expandable when dragging starts
			onExpandFlyoutToggle(false);

			// Keep track of whether we closed the expandable for drag
			expandableClosedForDrag.current = true;
		} else if (!isDragging && expandableClosedForDrag.current) {
			// If we were dragging and now we're not, reopen the expandable if it was closed
			onExpandFlyoutToggle(true);
			expandableClosedForDrag.current = false;
		}
	}, [
		isDragging,
		isExpandable,
		isExpandOrFlyoutOpen,
		isItemDraggable,
		expandableClosedForDrag,
		onExpandFlyoutToggle,
	]);

	return {
		draggableRef: isItemDraggable ? draggableRef : undefined,
		isItemDraggable,
		isDragging,
		dropTargetRef: isItemDraggable ? dropTargetRef : undefined,
		dropIndicator: isItemDraggable ? dropIndicator : undefined,
		maybeDragPreview: isItemDraggable ? dragPreview : null,
	};
}
